import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  GraduationCap,
  KeyRound,
  Sparkles,
  UserPlus,
  UsersRound,
} from "lucide-react";
import { collection, query, where, getDocs, doc, getDoc, addDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebaseConfig";
import { DashboardHeader, DashboardSidebar } from "./Dashboard";
import { formatJoinCodeInput, normalizeJoinCode } from "./utils/joinCodes";
import "./MyClasses.css";

const MyClasses = ({ user }) => {
  const navigate = useNavigate();
  const [myClasses, setMyClasses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  const [joinClassCode, setJoinClassCode] = useState("");
  const [joinMessage, setJoinMessage] = useState(null);
  const [isJoiningClass, setIsJoiningClass] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!user?.uid) {
      navigate("/");
      return;
    }

    const fetchMyClasses = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const membershipsRef = collection(db, "classMemberships");
        const classQuery = query(
          membershipsRef,
          where("studentUid", "==", user.uid),
          where("active", "==", true)
        );
        const snapshot = await getDocs(classQuery);
        const classList = snapshot.docs.map((membershipDoc) => ({
          id: membershipDoc.id,
          classId: membershipDoc.data().classId,
          className: membershipDoc.data().className,
          classCode: membershipDoc.data().classCode,
          teacherCode: membershipDoc.data().teacherCode,
          joinedAt: membershipDoc.data().joinedAt,
        }));
        setMyClasses(classList);
      } catch (loadError) {
        console.error("Error fetching my classes:", loadError);
        setError("Could not load your classes.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchMyClasses();
  }, [user, navigate]);

  useEffect(() => {
    const syncTheme = () => {
      const nextMode = localStorage.getItem("darkMode") === "true";
      setDarkMode(nextMode);
      document.body.classList.toggle("dark-mode", nextMode);
      document.documentElement.setAttribute("data-dark-mode", nextMode ? "true" : "false");
    };
    syncTheme();
    window.addEventListener("storage", syncTheme);
    return () => window.removeEventListener("storage", syncTheme);
  }, []);

  const formatDate = (dateObj) => {
    if (!dateObj) return "N/A";
    const date = dateObj.toDate ? dateObj.toDate() : new Date(dateObj);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleJoinClass = async () => {
    const trimmedCode = normalizeJoinCode(joinClassCode);
    if (!trimmedCode) {
      setJoinMessage({ type: "error", text: "Please enter a class code." });
      setTimeout(() => setJoinMessage(null), 3000);
      return;
    }
    setIsJoiningClass(true);
    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.data();
      if (!userData.teacherCode && !userData.teacherCodes) {
        setJoinMessage({ type: "error", text: "You must join a teacher before joining a class." });
        setTimeout(() => setJoinMessage(null), 3000);
        return;
      }
      const classesRef = collection(db, "classes");
      const classQuery = query(classesRef, where("classCode", "==", trimmedCode), where("active", "==", true));
      const classSnapshot = await getDocs(classQuery);
      if (classSnapshot.empty) {
        setJoinMessage({ type: "error", text: "Class code not found. Please check the code and try again." });
        setTimeout(() => setJoinMessage(null), 3000);
        return;
      }
      const classDoc = classSnapshot.docs[0];
      const classData = classDoc.data();
      const classId = classDoc.id;
      const studentTeacherCodes = userData.teacherCodes || [userData.teacherCode];
      const classTeacherCode = normalizeJoinCode(classData.teacherCode);
      const linkedToClassTeacher = Boolean(classTeacherCode) &&
        studentTeacherCodes.some((code) => normalizeJoinCode(code) === classTeacherCode);
      if (!linkedToClassTeacher) {
        setJoinMessage({ type: "error", text: "You must join this teacher before joining their class." });
        setTimeout(() => setJoinMessage(null), 3000);
        return;
      }
      const membershipsRef = collection(db, "classMemberships");
      const membershipQuery = query(membershipsRef, where("studentUid", "==", user.uid), where("classId", "==", classId), where("active", "==", true));
      const membershipSnapshot = await getDocs(membershipQuery);
      if (membershipSnapshot.size > 0) {
        setJoinMessage({ type: "info", text: "You have already joined this class." });
        setTimeout(() => setJoinMessage(null), 3000);
        return;
      }

      const currentUserDocRef = doc(db, "users", user.uid);
      const currentUserDocSnap = await getDoc(currentUserDocRef);
      const currentUserDocData = currentUserDocSnap.exists() ? currentUserDocSnap.data() : {};
      const studentName =
        currentUserDocData.displayName ||
        currentUserDocData.username ||
        user.displayName ||
        currentUserDocData.email ||
        user.email ||
        "Unknown Student";
      const studentEmail = currentUserDocData.email || user.email || "";

      const docRef = await addDoc(membershipsRef, {
        classId,
        classCode: classData.classCode,
        className: classData.className,
        teacherUid: classData.teacherUid,
        teacherCode: classData.teacherCode || "",
        studentUid: user.uid,
        studentName,
        studentEmail,
        active: true,
        joinedAt: serverTimestamp(),
      });
      setMyClasses((current) => {
        if (current.some((classItem) => classItem.classId === classId)) return current;
        return [...current, {
          id: docRef.id,
          classId,
          className: classData.className,
          classCode: classData.classCode,
          teacherCode: classData.teacherCode || "",
          joinedAt: new Date(),
        }];
      });
      setJoinMessage({ type: "success", text: `You have joined ${classData.className}.` });
      setJoinClassCode("");
      setTimeout(() => setJoinMessage(null), 3000);
    } catch (joinError) {
      console.error("Error joining class:", joinError);
      setJoinMessage({ type: "error", text: "Could not join class. Please try again." });
      setTimeout(() => setJoinMessage(null), 3000);
    } finally {
      setIsJoiningClass(false);
    }
  };

  const toggleTheme = () => {
    const nextMode = !darkMode;
    localStorage.setItem("darkMode", String(nextMode));
    setDarkMode(nextMode);
    document.body.classList.toggle("dark-mode", nextMode);
    document.documentElement.setAttribute("data-dark-mode", nextMode ? "true" : "false");
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (logoutError) {
      console.error("Error signing out", logoutError);
    }
  };

  const username = user?.displayName || user?.email?.split("@")[0] || "Student";
  const teacherCount = useMemo(() => new Set(
    myClasses.map((classItem) => classItem.teacherCode).filter(Boolean)
  ).size, [myClasses]);
  const visibleClasses = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return myClasses;
    return myClasses.filter((classItem) => [
      classItem.className,
      classItem.classCode,
      classItem.teacherCode,
    ].some((value) => String(value || "").toLowerCase().includes(needle)));
  }, [myClasses, search]);

  return (
    <div className={`sd-dashboard sd-brand-theme mc-page${darkMode ? " sd-dark" : ""}`}>
      <DashboardSidebar role="student" username={username} onLogout={handleLogout} officialDashboardBrand
        open={sidebarOpen} onClose={() => setSidebarOpen(false)} activePath="/my-classes" />
      <div className="sd-workspace mc-workspace">
        <DashboardHeader username={username} role="student" darkMode={darkMode}
          onToggleTheme={toggleTheme} onOpenMenu={() => setSidebarOpen(true)}
          search={search} onSearch={setSearch} />

        <main className="sd-main mc-main">
          <section className="mc-hero">
            <div className="mc-hero-copy">
              <p className="mc-kicker">Student classes</p>
              <h1>My Classes</h1>
              <p>View the classes you've joined and stay connected with your teachers.</p>
            </div>
            <div className="mc-hero-art" aria-hidden="true">
              <span><GraduationCap size={32} /></span>
              <i className="mc-orbit mc-orbit-one"><Sparkles size={17} /></i>
              <i className="mc-orbit mc-orbit-two"><BookOpen size={18} /></i>
              <i className="mc-orbit mc-orbit-three"><UsersRound size={17} /></i>
            </div>
          </section>

          <section className="mc-metrics" aria-label="Class summary">
            <article className="mc-metric mc-metric-classes"><span><GraduationCap size={22} /></span><div><strong>{myClasses.length}</strong><h2>Classes Joined</h2><p>Active class memberships</p></div></article>
            <article className="mc-metric mc-metric-teachers"><span><UsersRound size={22} /></span><div><strong>{teacherCount}</strong><h2>Teachers Connected</h2><p>Across your joined classes</p></div></article>
          </section>

          <section className="mc-join-panel">
            <div className="mc-panel-heading">
              <span className="mc-panel-icon"><UserPlus size={20} /></span>
              <div><h2>Join a Class</h2><p>Enter the class code your teacher gave you.</p></div>
            </div>
            <form className="mc-join-form" onSubmit={(event) => { event.preventDefault(); handleJoinClass(); }}>
              <label className="mc-code-field">
                <KeyRound size={18} />
                <input type="text" value={joinClassCode}
                  onChange={(event) => setJoinClassCode(formatJoinCodeInput(event.target.value))}
                  placeholder="Enter class code" aria-label="Class code" />
              </label>
              <button type="submit" disabled={isJoiningClass} className="mc-join-button">
                <UserPlus size={17} />{isJoiningClass ? "Joining..." : "Join Class"}
              </button>
            </form>
            {joinMessage && <div role="status" className={`mc-message mc-message-${joinMessage.type}`}>
              {joinMessage.text}
            </div>}
          </section>

          <section className="mc-classes-panel">
            <div className="mc-list-heading"><div><h2>Your classes</h2><p>{search.trim() ? `${visibleClasses.length} matching classes` : "Your active class connections"}</p></div><span>{myClasses.length} total</span></div>

            {isLoading && <div className="mc-state" role="status"><div className="mc-spinner" /><h2>Loading your classes...</h2></div>}
            {!isLoading && error && <div className="mc-state mc-state-error" role="alert"><AlertTriangle size={30} /><h2>We could not load your classes</h2><p>{error}</p></div>}
            {!isLoading && !error && myClasses.length === 0 && (
              <div className="mc-state"><GraduationCap size={34} /><h2>You haven't joined any classes yet.</h2><p>Use your teacher's code to connect, then enter a class code above to access assigned work.</p><button type="button" onClick={() => navigate("/join-teacher")}><UserPlus size={16} />Join Teacher</button></div>
            )}
            {!isLoading && !error && myClasses.length > 0 && visibleClasses.length === 0 && (
              <div className="mc-state mc-state-compact"><GraduationCap size={28} /><h2>No classes match your search.</h2></div>
            )}
            {!isLoading && !error && visibleClasses.length > 0 && (
              <div className="mc-class-grid">
                {visibleClasses.map((classItem, index) => (
                  <article key={classItem.id} className={`mc-class-card mc-tone-${index % 5}`}>
                    <div className="mc-class-topline"><span className="mc-class-icon"><GraduationCap size={22} /></span><span className="mc-active-badge"><CheckCircle2 size={13} />Active</span></div>
                    <h3>{classItem.className || "Class"}</h3>
                    <div className="mc-class-details">
                      <div><KeyRound size={15} /><span><small>Class code</small><strong>{classItem.classCode || "Not available"}</strong></span></div>
                      <div><UsersRound size={15} /><span><small>Teacher code</small><strong>{classItem.teacherCode || "Not available"}</strong></span></div>
                      {classItem.joinedAt && <div><CalendarDays size={15} /><span><small>Joined</small><strong>{formatDate(classItem.joinedAt)}</strong></span></div>}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </main>
        <footer className="mc-footer">© {new Date().getFullYear()} StudyDen. All rights reserved.</footer>
      </div>
    </div>
  );
};

export default MyClasses;
